import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './baseQuery';
import { IPagedResult } from '../../shared/models/paged-result';
import { IStore } from '../../shared/models/core/store';
import { WarehouseFilterSet } from '../../shared/models/sale/warehouse-filter-set';
import { serialize } from '../../utility/serialize';

export const storeApi = createApi({
  reducerPath: 'storeApi',
  baseQuery,
  tagTypes: ['Stores'],
  endpoints: (builder) => ({
    getStores: builder.query<IPagedResult<IStore>, WarehouseFilterSet>({
      query: (filterSet) => ({
        url: `/api/warehouses?${serialize(filterSet)}`,
        method: 'GET',
      }),
      providesTags: ['Stores'],
    }),
    getStore: builder.query<IStore, string>({
      query: (id) => ({
        url: `/api/warehouses/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'Stores', id }],
    }),
    addStore: builder.mutation<IStore, Omit<IStore, 'id'>>({
      query: (values) => ({
        url: `/api/warehouses`,
        method: 'POST',
        body: values,
      }),
      invalidatesTags: ['Stores'],
    }),
    updateStore: builder.mutation<IStore, IStore>({
      query: (values) => ({
        url: `/api/warehouses/${values.id}`,
        method: 'PUT',
        body: values,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Stores', id }],
    }),
    deleteStore: builder.mutation<void, string>({
      query: (id) => ({
        url: `/api/warehouses/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Stores'],
    }),
    exportWarehouseAsXslx: builder.query<Blob, string>({
      query: (filterSet) => {
        return {
          url: `/api/warehouses/:exportAsXslx?${filterSet}`,
          responseHandler: (response) => response.blob(),
        };
      },
    }),
  }),
});

export const {
  useGetStoresQuery,
  useLazyGetStoresQuery,
  useGetStoreQuery,
  useAddStoreMutation,
  useUpdateStoreMutation,
  useDeleteStoreMutation,
  useLazyExportWarehouseAsXslxQuery,
} = storeApi;
